import React,  { Fragment } from 'react'
import PCstyles from 'components/Onboarding/PageContainer/PageContainer.module.scss'
import {
  associateContactFields,
  nonRequiredContingentFields,
} from 'utils/formFieldLists'

const cta = (onClickFunction, text) => (
  <a
    className={PCstyles.highlightWord}
    onClick={onClickFunction}>
    {text}
  </a>
)

const successorCustodianDescription = (openModal) => {
  const handleOpenModal = () => openModal('successorCustodianInfoModal')
  return (
    <span>You can nominate a {cta(handleOpenModal, 'successor custodian ')}
      for this account.<br />
      <em>This is optional, and you can add a successor custodian at any point.</em>
    </span>
  )
}

const beneficiaryDescription = (openModal) => {
  const handleOpenModal = () => openModal('beneficiaryInfoModal')
  return (
    <Fragment>
      <span>
        You can designate one or multiple {cta(handleOpenModal, 'beneficiaries ')}
        for this account.
      </span>
      <br />
      <span>
        <em>
        This is optional, and you can add a beneficiary at any point.
        </em>
      </span>
    </Fragment>
  )
}

const trusteeDescription = (openModal) => {
  const handleOpenModal = () => openModal('trusteeInfoModal')
  return (
    <span>
      Designate up to 10 additional {cta(handleOpenModal, 'trustees')}.
      Adding more trustees is optional — if you are the only trustee, skip this section.
    </span>
  )
}

const grantorDescription = (openModal) => {
  const handleOpenModal = () => openModal('grantorInfoModal')
  return (
    <span>
      Designate any number of {cta(handleOpenModal, ' grantors ')}
      of this trust.
      If you are a grantor, select yourself from the dropdown.
    </span>
  )
}

const objectFlip = (obj) => {
  const ret = {}
  Object.keys(obj).forEach((key) => {
    ret[obj[key]] = key
  })
  return ret
}

export const recordTypeFilteredContacts = (contactType, contacts) => {
  const { allowedContactRecordTypes } = allContactTypes[contactType]

  return contacts.filter(c => allowedContactRecordTypes.includes(c.recordType) || !c.recordType)
}

export const selfAsOptionFilteredContacts = (contactType, contacts, prospect) => {
  const { selfAsContactAllowed } = allContactTypes[contactType]

  if (selfAsContactAllowed) {
    return contacts
  }

  return contacts.filter(c => c.id !== prospect.personContactId)
}

export const contactsFilteredByTypeRules = (contactType, contacts, prospect) => {
  const allowedContactsByRecordType = recordTypeFilteredContacts(contactType, contacts)
  return selfAsOptionFilteredContacts(
    contactType,
    allowedContactsByRecordType,
    prospect
  )
}

export const CONTACT_RECORD_TYPES = {
  ORGANIZATION: 'Organization',
  TRUST: 'Trust',
  BUSINESS: 'Business',
  ASSOCIATE: 'Associate',
}

export const CONTACT_RECORD_TYPE_MAPPING = {
  Organization: 'Organization',
  Trust: 'Trust',
  Business: 'Business',
  Associate: 'Individual'
}

export const CONTACT_RECORD_TYPE_DEMAPPING = objectFlip(CONTACT_RECORD_TYPE_MAPPING)

const sensitiveFieldsMapping = {
  ssn: 'lastFourDigitSsn',
  identificationNumber: 'lastFourDigitIdentificationNumber',
  taxIdentificationNumber: 'lastFourDigitTaxId'
}

export const contactComplete = (contactType, contact) => {
  const { fields } = allContactTypes[contactType]
  const { recordType } = contact

  let fieldsByRecordType = [...fields[recordType || 'Associate']]

  const contingentFieldsToRemove = nonRequiredContingentFields(contact, false)
  fieldsByRecordType = fieldsByRecordType.filter(f => !contingentFieldsToRemove.includes(f))

  Object.keys(sensitiveFieldsMapping).forEach((f) => {
    const index = fieldsByRecordType.indexOf(f)
    const nonSensitiveCounterpart = sensitiveFieldsMapping[f]

    if (index !== -1) {
      fieldsByRecordType[index] = nonSensitiveCounterpart
    }
  })

  return fieldsByRecordType.every(f => !!contact[f])
}

const successorCustodiansDescription = 'Enter your successor custodian’s personal information below. Remember that this is a nomination only.'

export const successorCustodians = {
  DISPLAY: 'Successor Custodian',
  role: 'Successor Custodian',
  description: openModal => successorCustodianDescription(openModal),
  addDescription: successorCustodiansDescription,
  editDescription: successorCustodiansDescription,
  section: 'SuccessorCustodian',
  reducerType: 'successorCustodians',
  routeType: 'successor_custodians',
  singularType: 'successor_custodian',
  fields: {
    Associate: [
      'firstName',
      'lastName',
      'ssn',
      'mailingStreet',
      'mailingCity',
      'mailingState',
      'mailingPostalCode',
      'mailingCountry',
    ]
  },
  modalName: 'successorCustodianInfoModal',
  maxContacts: 1,
  selfAsContactAllowed: false,
  allowedContactRecordTypes: [CONTACT_RECORD_TYPES.ASSOCIATE]
}

export const beneficiaries = {
  DISPLAY: 'Beneficiary',
  role: 'Beneficiary',
  description: openModal => beneficiaryDescription(openModal),
  addDescription: 'Enter your beneficiary’s information below.',
  editDescription: 'Please edit your beneficiary’s information below.',
  section: 'Beneficiary',
  reducerType: 'beneficiaries',
  routeType: 'beneficiaries',
  singularType: 'beneficiary',
  fields: {
    Associate: [
      'firstName',
      'middleName',
      'lastName',
      'birthdate',
      'mailingStreet',
      'mailingCity',
      'mailingState',
      'mailingPostalCode',
      'mailingCountry',
      'mobilePhone',
      'email',
      'ssn',
      'relationship',
      'countryOfIncorporation'
    ],
    Organization: [
      'organizationName',
      'taxIdentificationNumber'
    ],
    Trust: [
      'trustName',
      'dateOfTrust',
      'taxIdentificationNumber'
    ]
  },
  overrideRequired: [
    ['email', true],
    ['birthdate', true],
  ],
  modalName: 'beneficiaryInfoModal',
  maxContacts: 10,
  selfAsContactAllowed: false,
  allowedContactRecordTypes: [
    CONTACT_RECORD_TYPES.ASSOCIATE,
    CONTACT_RECORD_TYPES.ORGANIZATION,
    CONTACT_RECORD_TYPES.TRUST
  ]
}

export const successorDonor = {
  ...beneficiaries,
  description: () => null,
  DISPLAY: 'Successor Donor',
  addDescription: 'Enter your successor donor’s information below.',
  editDescription: 'Please edit your successor donor’s information below.',
}

export const trustees = {
  DISPLAY: 'Trustee',
  role: 'Trustee',
  description: openModal => trusteeDescription(openModal),
  addDescription: 'Enter the trustee’s information below.',
  editDescription: 'Please edit your trustee’s information below.',
  section: 'Trustee',
  reducerType: 'trustees',
  routeType: 'trustees',
  singularType: 'trustee',
  fields: {
    Associate: [
      ...associateContactFields
    ]
  },
  modalName: 'trusteeInfoModal',
  maxContacts: 10,
  selfAsContactAllowed: false,
  allowedContactRecordTypes: [CONTACT_RECORD_TYPES.ASSOCIATE]
}

const grantorEntityFields = [
  'lastName',
  'mailingStreet',
  'mailingCity',
  'mailingState',
  'mailingPostalCode',
  'mailingCountry',
  'taxIdentificationNumber',
  'publicCompanyAffiliated',
  'countryOfIncorporation',
  'stateOfIncorporation'
]

export const grantors = {
  DISPLAY: 'Grantor',
  role: 'Grantor',
  description: openModal => grantorDescription(openModal),
  addDescription: 'Enter the grantor’s information below.',
  editDescription: 'Please edit your grantor’s information below.',
  section: 'Beneficiary',
  reducerType: 'grantors',
  routeType: 'grantors',
  singularType: 'grantor',
  fields: {
    Associate: [
      'firstName',
      'lastName',
      'birthdate',
      'ssn',
      'mailingStreet',
      'mailingCity',
      'mailingState',
      'mailingPostalCode',
      'mailingCountry',
      'mobilePhone',
      'primaryCitizenship',
      'countryOfResidence',
      'countryOfBirth',
      'identificationType',
      'identificationNumber',
      'localeOfIdIssuance',
      'identificationIssueDate',
      'identificationExpirationDate',
      'finraAffiliated',
      'publicCompanyAffiliated',
      'grantorDeceased',
      'dateOfDeath'
    ],
    Organization: [
      ...grantorEntityFields,
      'organizationName'
    ],
    Trust: [
      ...grantorEntityFields,
      'trustName',
      'dateOfTrust',
    ]
  },
  modalName: 'grantorInfoModal',
  maxContacts: 10,
  selfAsContactAllowed: true,
  validEntityTypes: [],
  allowedContactRecordTypes: [
    CONTACT_RECORD_TYPES.ASSOCIATE,
    CONTACT_RECORD_TYPES.ORGANIZATION,
    CONTACT_RECORD_TYPES.TRUST
  ]
}

export const allContactTypes = {
  successor_custodians: successorCustodians,
  beneficiaries,
  successor_donor: successorDonor,
  trustees,
  grantors
}

export const entities = ['Organization', 'Trust']
export const isValidContactType = contactType => (
  !!Object.keys(allContactTypes).find(e => contactType === e)
)



// WEBPACK FOOTER //
// ./src/utils/contactTypes.js